import { h, Fragment } from 'preact'
import { useState, useMemo } from 'preact/hooks'

import { useAppState } from 'lib/appState'
import { useAdminOrganizations, useConvertNetworkType } from 'lib/networkHooks'

import Modal from 'components/Modal'
import Header from 'components/Header'
import Button from 'components/Button'
import Icon from 'components/Icon'
import OrganizationIcon from 'components/OrganizationIcon'
import Loading from 'components/Loading'

import './index.sass'

export default function ConvertNetworkTypeModal({ organization, isOpen, onClose }) {
  const { adminOrganizations, loadingAdminOrganizations } = useAdminOrganizations('ConvertNetworkTypeModal')
  const { convertNetworkType } = useConvertNetworkType('ConvertNetworkTypeModal')
  const { takeAction } = useAppState({}, 'ConvertNetworkTypeModal')

  const [isConverting, setIsConverting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [selectedRole, setSelectedRole] = useState('master')
  const [selectedParentApikey, setSelectedParentApikey] = useState(null)

  const currentType = organization.network_type || 'legacy'
  const targetType = currentType === 'legacy' ? 'hierarchical' : 'legacy'

  const eligibleParentOrgs = useMemo(() => {
    if (!adminOrganizations || targetType !== 'hierarchical') return []

    return adminOrganizations.filter(org =>
      org.apikey !== organization.apikey &&
      org.network_type === 'hierarchical'
    )
  }, [adminOrganizations, organization.apikey, targetType])

  const hasChildren = useMemo(() => {
    if (!adminOrganizations || currentType !== 'hierarchical') return false

    return adminOrganizations.some(org => org.parent_organization_apikey === organization.apikey)
  }, [adminOrganizations, organization.apikey, currentType])

  const handleConvert = () => {
    if (targetType === 'hierarchical' && selectedRole === 'sub' && !selectedParentApikey) {
      setErrorMessage('Please select a parent hub')
      return
    }

    setIsConverting(true)
    setErrorMessage(null)
    convertNetworkType({
      organizationApikey: organization.apikey,
      targetNetworkType: targetType,
      isMasterOrg: targetType === 'hierarchical' ? selectedRole === 'master' : undefined,
      parentOrganizationApikey: selectedRole === 'sub' ? selectedParentApikey : undefined,
      onSuccess: () => {
        setIsConverting(false)
        onClose()
        takeAction('network.loadAdminOrganizations')
      },
      onError: (error) => {
        setIsConverting(false)
        setErrorMessage(error?.message || 'Failed to convert network type')
      },
    })
  }

  if (!isOpen) return null

  return <Modal
    className="ConvertNetworkTypeModal"
    onClose={onClose}
    closeOnOutsideClick={!isConverting}
  >
    <div className="ConvertNetworkTypeModal-content">
      {errorMessage && (
        <div className="ConvertNetworkTypeModal-error">
          <Icon type="alert" size="md" />
          <div>
            <Header size="md">Error</Header>
            <p>{errorMessage}</p>
          </div>
        </div>
      )}

      {targetType === 'hierarchical' ? (
        <HierarchicalConversionContent
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          selectedParentApikey={selectedParentApikey}
          setSelectedParentApikey={setSelectedParentApikey}
          eligibleParentOrgs={eligibleParentOrgs}
          loadingAdminOrganizations={loadingAdminOrganizations}
        />
      ) : (
        <LegacyConversionContent hasChildren={hasChildren} />
      )}

      <div className="ConvertNetworkTypeModal-actions">
        <Button
          type="normal"
          onClick={onClose}
          disabled={isConverting}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleConvert}
          disabled={
            isConverting ||
            (targetType === 'hierarchical' && selectedRole === 'sub' && !selectedParentApikey) ||
            (targetType === 'legacy' && hasChildren)
          }
          loading={isConverting}
        >
          Confirm Switch
        </Button>
      </div>
    </div>
  </Modal>
}

function HierarchicalConversionContent({
  selectedRole,
  setSelectedRole,
  selectedParentApikey,
  setSelectedParentApikey,
  eligibleParentOrgs,
  loadingAdminOrganizations
}) {
  const masterRoleClass = [
    'ConvertNetworkTypeModal-roleOption',
    selectedRole === 'master' ? 'selected' : ''
  ].join(' ')

  return <Fragment>
    <Header size="lg">
      <Icon type="treeStructure" /> Converting to Hierarchical Network
    </Header>

    <p className="ConvertNetworkTypeModal-description">
      Choose the role for this organization in the hierarchical structure.
    </p>

    <div className="ConvertNetworkTypeModal-roleSelection">
      <div
        className={masterRoleClass}
        onClick={() => setSelectedRole('master')}
      >
        <div className="ConvertNetworkTypeModal-roleHeader">
          <Icon type="org" size="md" />
          <Header size="md">Prime Hub</Header>
        </div>
        <p className="ConvertNetworkTypeModal-roleDescription">
          Top-level hub that can have hubs beneath it. Admins inherit access to all hubs.
        </p>
      </div>

      <div
        className={`ConvertNetworkTypeModal-roleOption ${selectedRole === 'sub' ? 'selected' : ''}`}
        onClick={() => setSelectedRole('sub')}
      >
        <div className="ConvertNetworkTypeModal-roleHeader">
          <Icon type="subdirectory" size="md" />
          <Header size="md">Hub</Header>
        </div>
        <p className="ConvertNetworkTypeModal-roleDescription">
          Nested hub under a parent hub. Inherits admins from parent. Can have its own hubs.
        </p>
      </div>
    </div>

    {selectedRole === 'sub' && (
      <div className="ConvertNetworkTypeModal-parentSelection">
        <Header size="md">Select Parent Hub</Header>
        {loadingAdminOrganizations ? (
          <Loading type="block" />
        ) : eligibleParentOrgs.length === 0 ? (
          <div className="ConvertNetworkTypeModal-noParents">
            <Icon type="alert" size="sm" />
            <span>No eligible parent hubs found. You need at least one hierarchical network to create a hub.</span>
          </div>
        ) : (
          <div className="ConvertNetworkTypeModal-parentList">
            {eligibleParentOrgs.map(org => {
              const itemClass = [
                'ConvertNetworkTypeModal-parentItem',
                selectedParentApikey === org.apikey ? 'selected' : ''
              ].join(' ')

              return (
                <div
                  key={org.apikey}
                  className={itemClass}
                  onClick={() => setSelectedParentApikey(org.apikey)}
                >
                  <OrganizationIcon organization={org} size="sm" bordered />
                  <div className="ConvertNetworkTypeModal-parentInfo">
                    <div className="ConvertNetworkTypeModal-parentName">{org.name}</div>
                    {org.tag_line && (
                      <div className="ConvertNetworkTypeModal-parentTagLine">{org.tag_line}</div>
                    )}
                  </div>
                  {selectedParentApikey === org.apikey && (
                    <Icon type="checkCircle" size="sm" />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )}

    <div className="ConvertNetworkTypeModal-consequences">
      <Header size="sm">Key Changes:</Header>
      <ul>
        {selectedRole === 'master' ? (
          <Fragment>
            <li>• This organization becomes a top-level prime hub</li>
            <li>• Can have hubs created beneath it</li>
            <li>• Admins automatically inherit access to all hubs</li>
          </Fragment>
        ) : (
          <Fragment>
            <li>• This organization becomes a hub</li>
            <li>• Parent hub admins automatically get access</li>
            <li>• Can create its own hubs if needed</li>
          </Fragment>
        )}
        <li>• Existing admins and curators are preserved</li>
      </ul>
    </div>
  </Fragment>
}

function LegacyConversionContent({ hasChildren }) {
  return <Fragment>
    <Header size="lg">
      <Icon type="list" /> Converting to Legacy Network
    </Header>

    <p className="ConvertNetworkTypeModal-description">
      This will remove all hierarchical relationships and admin inheritance.
    </p>

    {hasChildren && (
      <div className="ConvertNetworkTypeModal-error">
        <Icon type="alert" size="md" />
        <div>
          <Header size="md">Cannot Convert to Legacy</Header>
          <p>
            This organization has hubs beneath it. You must first move or
            delete all hubs before converting to legacy.
          </p>
        </div>
      </div>
    )}

    {!hasChildren && (
      <Fragment>
        <div className="ConvertNetworkTypeModal-consequences">
          <Header size="sm">This action will:</Header>
          <ul>
            <li>• Remove parent-child relationships</li>
            <li>• Disable admin inheritance</li>
            <li>• Make this an independent organization</li>
            <li>• Preserve current admin assignments</li>
          </ul>
        </div>

        <div className="ConvertNetworkTypeModal-warning">
          <Icon type="alert" size="sm" />
          <p>
            <strong>This change cannot be undone.</strong> Make sure you understand the impact before proceeding.
          </p>
        </div>
      </Fragment>
    )}
  </Fragment>
}

